import type {
  CommunityArtifact,
  LangCode,
  TrainingArtifact,
  TrainingArtifactKind,
  TrainingAudioLength,
  TrainingResource,
  UserTrainingReport,
} from "@scoach/types";
import { Modal, Spinner, useToast } from "@scoach/ui";
import {
  Bolt,
  Brain,
  Chart,
  Check,
  Doc,
  Globe,
  Image as ImageIcon,
  Play,
  Plus,
  Question,
  Spark,
  Trend,
  Users,
  Video,
} from "@scoach/ui/icons";
import { useEffect, useRef, useState } from "react";

import { LANGUAGES } from "@scoach/types";
import { safeHref } from "../../../lib/safeHref.ts";
import { authApi } from "../../auth/api.ts";
import { useAuthStore } from "../../auth/store.ts";
import { DashHeader } from "../../dashboard/components/DashHeader.tsx";
import { type TrainingRefreshMode, trainingApi } from "../api.ts";

const SEV_COLOR: Record<string, string> = {
  high: "var(--gc-red)",
  med: "var(--gc-orange)",
  low: "var(--text-4)",
};
const SEV_LABEL: Record<string, string> = { high: "High", med: "Medium", low: "Low" };

const KIND_META: Record<TrainingArtifactKind, { label: string; icon: React.ReactNode }> = {
  audio: { label: "Podcast", icon: <Play size={15} /> },
  slides: { label: "Slides", icon: <Doc size={15} /> },
  quiz: { label: "Quiz", icon: <Question size={15} /> },
  image: { label: "Infographic", icon: <ImageIcon size={15} /> },
  video: { label: "Video", icon: <Video size={15} /> },
};
const GENERATORS: TrainingArtifactKind[] = ["audio", "slides", "quiz", "image"];

const DEFAULT_PROMPT: Record<TrainingArtifactKind, string> = {
  audio:
    "Create a short, friendly podcast that teaches me the key points of my training plan, focused on practical sales skills and product mastery.",
  slides: "Create a concise slide deck on my training plan's top topics, with practical examples.",
  quiz: "Create a practice quiz that tests me on my training plan's key topics.",
  image: "Create an infographic summarizing my training plan's key topics and how they connect.",
  video: "Create a short lesson on my training plan's key topics.",
};

function resIcon(t: TrainingResource["type"]) {
  if (t === "video") return <Video size={15} />;
  if (t === "doc") return <Doc size={15} />;
  return <Globe size={15} />;
}

type ViewerItem =
  | { mode: "artifact"; id: string; kind: TrainingArtifactKind; title?: string; url?: string }
  | {
      mode: "resource";
      id: string;
      rtype: TrainingResource["type"];
      title?: string;
      url: string;
      videoId?: string;
    };
interface Viewer {
  item: ViewerItem;
  blobUrl?: string;
  loading: boolean;
  error?: string;
}

/** A queued content-generation request (processed one at a time). */
interface QueueJob {
  id: string;
  kind: TrainingArtifactKind;
  topic?: string;
  prompt?: string;
  length?: TrainingAudioLength;
  language?: "auto" | LangCode;
  label: string;
  status: "queued" | "running";
}

/** Podcast length options shown when generating audio. */
const AUDIO_LENGTHS: { value: TrainingAudioLength; label: string; hint: string }[] = [
  { value: "short", label: "Quick clip", hint: "~1 min" },
  { value: "standard", label: "Podcast", hint: "2-3 min" },
  { value: "long", label: "Deep dive", hint: "8-12 min" },
];
let queueSeq = 0;

export function TrainingScreen() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const toast = useToast();
  const [report, setReport] = useState<UserTrainingReport | null>(null);
  const [community, setCommunity] = useState<CommunityArtifact[]>([]);
  const [completed, setCompleted] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<TrainingRefreshMode>("meetings");
  const [refreshing, setRefreshing] = useState(false);
  const [moreLoading, setMoreLoading] = useState(false);
  const [resLoading, setResLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Set<number>>(new Set());
  const [viewer, setViewer] = useState<Viewer | null>(null);

  // Add-content modal
  const [addOpen, setAddOpen] = useState(false);
  const [addKind, setAddKind] = useState<TrainingArtifactKind>("slides");
  const [addLength, setAddLength] = useState<TrainingAudioLength>("standard");
  // Preferred language for ALL generated materials (persisted to the user's settings).
  const [contentLang, setContentLang] = useState<"auto" | LangCode>(
    user?.settings?.trainingLanguage ?? "auto",
  );
  const [addTopic, setAddTopic] = useState<string | undefined>(undefined);
  const [addPrompt, setAddPrompt] = useState("");
  const [related, setRelated] = useState<TrainingResource[]>([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [addErr, setAddErr] = useState<string | null>(null);
  // Generation queue — process one at a time; never block the user with a cooldown error.
  const [queue, setQueue] = useState<QueueJob[]>([]);
  const runningRef = useRef(false);
  // Recently-finished items (for the floating widget); newest first.
  const [recentDone, setRecentDone] = useState<TrainingArtifact[]>([]);
  const [widgetMin, setWidgetMin] = useState(false);

  function applyResponse(r: {
    report: UserTrainingReport | null;
    completed: string[];
    community: CommunityArtifact[];
  }) {
    setReport(r.report);
    setCompleted(new Set(r.completed));
    setCommunity(r.community);
  }

  useEffect(() => {
    trainingApi
      .get()
      .then((r) => {
        applyResponse(r);
        if (r.report?.source === "role") setMode("role");
      })
      .catch((e) => setErr((e as Error).message))
      .finally(() => setLoading(false));
  }, []);

  async function onRefresh(m: TrainingRefreshMode) {
    setMode(m);
    setRefreshing(true);
    setErr(null);
    setNote(null);
    try {
      const r = await trainingApi.refresh(m);
      applyResponse(r);
      if (m === "meetings" && !r.report) {
        setNote("Not enough meetings to analyze yet — switch to “My role” for a role-based plan.");
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setRefreshing(false);
    }
  }

  async function onMore() {
    setMoreLoading(true);
    setErr(null);
    try {
      applyResponse(await trainingApi.more());
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setMoreLoading(false);
    }
  }

  async function onFindResources() {
    setResLoading(true);
    setErr(null);
    try {
      applyResponse(await trainingApi.resources());
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setResLoading(false);
    }
  }

  /** Change + persist the preferred language for generated materials. */
  function changeContentLang(next: "auto" | LangCode) {
    setContentLang(next);
    if (user) setUser({ ...user, settings: { ...user.settings, trainingLanguage: next } });
    void authApi.updateSettings({ trainingLanguage: next }).catch(() => {});
  }

  function openAdd(kind: TrainingArtifactKind, topic?: string) {
    setAddKind(kind);
    setAddLength("standard");
    setAddTopic(topic);
    setAddPrompt(topic ? `Focus on: ${topic}. ${DEFAULT_PROMPT[kind]}` : DEFAULT_PROMPT[kind]);
    setRelated([]);
    setAddErr(null);
    setAddOpen(true);
  }

  async function loadRelated() {
    setRelatedLoading(true);
    try {
      const topic = addTopic || report?.skillGaps[0]?.area || `${report?.role} essentials`;
      setRelated(await trainingApi.findResources(topic));
    } catch {
      setRelated([]);
    } finally {
      setRelatedLoading(false);
    }
  }

  /** Enqueue a generation and close the modal — the queue processes it in the
   * background (no cooldown errors). A toast fires when it's ready. */
  function enqueueGenerate(
    kind: TrainingArtifactKind,
    topic?: string,
    prompt?: string,
    length?: TrainingAudioLength,
    language?: "auto" | LangCode,
  ) {
    queueSeq += 1;
    const lenLabel =
      kind === "audio" && length && length !== "standard"
        ? (AUDIO_LENGTHS.find((l) => l.value === length)?.label ?? KIND_META[kind].label)
        : KIND_META[kind].label;
    const job: QueueJob = {
      id: `q${queueSeq}`,
      kind,
      topic,
      prompt: prompt?.trim() || undefined,
      length: kind === "audio" ? length : undefined,
      language,
      label: topic || lenLabel,
      status: "queued",
    };
    setQueue((q) => [...q, job]);
  }

  function runGenerate() {
    enqueueGenerate(addKind, addTopic, addPrompt, addLength, contentLang);
    setAddOpen(false);
  }

  // Drain the queue one job at a time (serial → no cooldown 429; retries once on it).
  useEffect(() => {
    if (runningRef.current) return;
    const next = queue.find((j) => j.status === "queued");
    if (!next) return;
    runningRef.current = true;
    setQueue((q) => q.map((j) => (j.id === next.id ? { ...j, status: "running" } : j)));
    (async () => {
      const label = (
        next.kind === "audio" && next.length && next.length !== "standard"
          ? (AUDIO_LENGTHS.find((l) => l.value === next.length)?.label ?? KIND_META[next.kind].label)
          : KIND_META[next.kind].label
      ).toLowerCase();
      try {
        let artifact: TrainingArtifact | null = null;
        for (let attempt = 0; attempt < 2 && !artifact; attempt++) {
          try {
            const r = await trainingApi.generateArtifact(
              next.kind,
              next.topic,
              next.prompt,
              next.length,
              next.language,
            );
            artifact = r.artifact;
          } catch (e) {
            // A cooldown/429 → wait briefly and retry silently (never shown to the user).
            const status = (e as { status?: number }).status;
            if (status === 429 && attempt === 0) {
              await new Promise((res) => setTimeout(res, 9000));
            } else {
              throw e;
            }
          }
        }
        if (artifact) {
          const done = artifact;
          setReport((prev) => (prev ? { ...prev, artifacts: [done, ...prev.artifacts] } : prev));
          setRecentDone((prev) => [done, ...prev].slice(0, 6));
          toast.push({
            tone: "success",
            message: `Your ${label}${next.topic ? ` on “${next.topic}”` : ""} is ready — see Study materials.`,
          });
        }
      } catch {
        toast.push({ tone: "error", message: `Couldn't create the ${label}. Please try again.` });
      } finally {
        setQueue((q) => q.filter((j) => j.id !== next.id));
        runningRef.current = false;
      }
    })();
  }, [queue, toast]);

  async function toggleDone(id: string) {
    const done = !completed.has(id);
    setCompleted((prev) => {
      const next = new Set(prev);
      if (done) next.add(id);
      else next.delete(id);
      return next;
    });
    try {
      const r = await trainingApi.complete(id, done);
      setCompleted(new Set(r.completed));
    } catch {
      /* keep optimistic state */
    }
  }

  async function openArtifact(a: {
    id: string;
    kind: TrainingArtifactKind;
    title?: string;
    url?: string;
  }) {
    if (!a.url) return;
    setViewer({ item: { mode: "artifact", ...a }, loading: true });
    try {
      const { blobUrl } = await trainingApi.loadArtifact(a.url);
      setViewer({ item: { mode: "artifact", ...a }, blobUrl, loading: false });
    } catch (e) {
      setViewer({ item: { mode: "artifact", ...a }, loading: false, error: (e as Error).message });
    }
  }

  function openResource(r: TrainingResource) {
    // Videos embed in-page. Articles/docs can't be framed (sites refuse with
    // X-Frame-Options), so open them directly in a new tab.
    if (r.type === "video" && r.videoId) {
      setViewer({
        item: {
          mode: "resource",
          id: r.id,
          rtype: "video",
          title: r.title,
          url: r.url,
          videoId: r.videoId,
        },
        loading: false,
      });
    } else {
      const href = safeHref(r.url);
      if (href) window.open(href, "_blank", "noopener");
    }
  }

  function closeViewer() {
    if (viewer?.blobUrl) URL.revokeObjectURL(viewer.blobUrl);
    setViewer(null);
  }

  const trackable = report
    ? [
        ...report.artifacts.map((a) => a.id),
        ...(report.resources ?? []).map((r) => r.id),
        ...community.map((c) => c.id),
      ]
    : [];
  const doneCount = trackable.filter((id) => completed.has(id)).length;
  const pct = trackable.length ? Math.round((doneCount / trackable.length) * 100) : 0;
  const weeklyFocus =
    report?.skillGaps.find((g) => g.severity === "high") ?? report?.skillGaps[0] ?? null;

  /** Materials generated FOR a specific topic (matched by title). */
  function artifactsFor(topic: string): TrainingArtifact[] {
    const t = topic.trim().toLowerCase();
    return report?.artifacts.filter((a) => (a.title ?? "").trim().toLowerCase() === t) ?? [];
  }

  /** Row of small chips linking to materials created for this topic. */
  function MadeChips({ topic }: { topic: string }) {
    const items = artifactsFor(topic);
    if (items.length === 0) return null;
    return (
      <div className="trn-made">
        <span className="trn-made-lbl">Created:</span>
        {items.map((a) => (
          <button key={a.id} type="button" className="trn-made-chip" onClick={() => openArtifact(a)}>
            {KIND_META[a.kind]?.icon} {KIND_META[a.kind]?.label}
          </button>
        ))}
      </div>
    );
  }

  function DoneBtn({ id }: { id: string }) {
    const done = completed.has(id);
    return (
      <button
        type="button"
        className={`trn-done ${done ? "on" : ""}`}
        title={done ? "Marked done" : "Mark as done"}
        onClick={(e) => {
          e.stopPropagation();
          void toggleDone(id);
        }}
      >
        <Check size={13} /> {done ? "Done" : "Mark done"}
      </button>
    );
  }

  return (
    <div className="dash">
      <DashHeader />
      <div className="trn-wrap">
        {/* Hero */}
        <div className="trn-hero">
          <div className="trn-hero-icon">
            <Brain size={24} />
          </div>
          <div>
            <h1>My ongoing training</h1>
            <div className="trn-hero-sub">
              {user?.role && <span className="trn-chip blue">{user.role}</span>}
              {report && (
                <span className="trn-chip">
                  {report.source === "role" ? "Role-based plan" : "From your meetings"}
                </span>
              )}
              {report && <span>Updated {new Date(report.generatedAt).toLocaleDateString()}</span>}
            </div>
          </div>
        </div>

        {/* Mode selector */}
        <div className="trn-modes">
          <button
            type="button"
            className={`trn-mode ${mode === "meetings" ? "on" : ""}`}
            disabled={refreshing}
            onClick={() => void onRefresh("meetings")}
          >
            <span className="trn-mode-ic">
              <Trend size={18} />
            </span>
            <span className="trn-mode-body">
              <span className="trn-mode-t">From my meetings</span>
              <span className="trn-mode-d">
                Analyze your recent calls to find where to grow and what to study.
              </span>
            </span>
          </button>
          <button
            type="button"
            className={`trn-mode ${mode === "role" ? "on" : ""}`}
            disabled={refreshing}
            onClick={() => void onRefresh("role")}
          >
            <span className="trn-mode-ic">
              <Bolt size={18} />
            </span>
            <span className="trn-mode-body">
              <span className="trn-mode-t">From my role</span>
              <span className="trn-mode-d">
                A fresh curriculum tailored for your role — great when you're new or have few meetings.
              </span>
            </span>
          </button>
        </div>
        {refreshing && (
          <div className="trn-generating">
            <Spark size={14} /> Generating your plan…
          </div>
        )}

        {note && (
          <div className="trn-note" style={{ marginTop: 14 }}>
            {note}
          </div>
        )}
        {err && (
          <div className="trn-error" style={{ marginTop: 14 }}>
            {err}
          </div>
        )}

        {loading ? (
          <div className="trn-empty" style={{ marginTop: 16 }}>
            Loading your training…
          </div>
        ) : !report ? (
          <div className="trn-empty" style={{ marginTop: 16 }}>
            Pick an option above to generate your personalized training plan.
          </div>
        ) : (
          <>
            {report.summary && (
              <div className="trn-summary">
                <p>{report.summary}</p>
                {report.source === "meetings" && (
                  <div className="trn-meta">
                    Based on {report.meetingsAnalyzed} meeting
                    {report.meetingsAnalyzed === 1 ? "" : "s"}.
                  </div>
                )}
              </div>
            )}

            {trackable.length > 0 && (
              <div className="trn-progress">
                <div className="trn-progress-top">
                  <span>Your progress</span>
                  <span>
                    {doneCount}/{trackable.length} done · {pct}%
                  </span>
                </div>
                <div className="trn-progress-bar">
                  <div className="trn-progress-fill" style={{ width: `${pct}%` }} />
                </div>
              </div>
            )}

            {weeklyFocus && (
              <div className="trn-focus">
                <div>
                  <div className="trn-focus-k">This week's focus</div>
                  <div className="trn-focus-t">{weeklyFocus.area}</div>
                  <div className="trn-focus-d">{weeklyFocus.why}</div>
                </div>
                <div className="trn-focus-actions">
                  <button
                    type="button"
                    className="pill-btn sm primary"
                    onClick={() => openAdd("slides", weeklyFocus.area)}
                  >
                    Make a lesson
                  </button>
                  <button
                    type="button"
                    className="pill-btn sm"
                    onClick={() => openAdd("quiz", weeklyFocus.area)}
                  >
                    Quiz me
                  </button>
                </div>
              </div>
            )}

            {/* Recommended videos & reading — grid with previews */}
            <section className="trn-section">
              <h2 className="trn-section-head">
                <Video size={17} /> Recommended videos &amp; reading
              </h2>
              {report.resources && report.resources.length > 0 ? (
                <div className="trn-vidgrid">
                  {report.resources.map((r) => (
                    <div key={r.id} className={`trn-card ${completed.has(r.id) ? "done" : ""}`}>
                      <button
                        type="button"
                        className="trn-card-media"
                        onClick={() => openResource(r)}
                      >
                        {r.thumbnail ? (
                          <img src={r.thumbnail} alt="" loading="lazy" />
                        ) : (
                          <div className="trn-card-ph">{resIcon(r.type)}</div>
                        )}
                        {r.type === "video" && (
                          <span className="trn-card-play">
                            <Play size={22} />
                          </span>
                        )}
                        <span className="trn-card-type">{r.type}</span>
                      </button>
                      <div className="trn-card-body">
                        <button
                          type="button"
                          className="trn-card-title"
                          onClick={() => openResource(r)}
                        >
                          {r.title}
                        </button>
                        <div className="trn-card-meta">
                          {r.source ? `${r.source} · ` : ""}
                          {r.why}
                        </div>
                        <div className="trn-card-foot">
                          <DoneBtn id={r.id} />
                          {safeHref(r.url) && (
                            <a
                              href={safeHref(r.url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="trn-openext"
                            >
                              Open ↗
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 12.5, color: "var(--text-3)", marginBottom: 10 }}>
                  Find real, current videos and articles (YouTube, authoritative blogs & docs, Medium)
                  matched to your role and growth areas — verified so they always work.
                </div>
              )}
              <button
                type="button"
                className="pill-btn sm"
                style={{ marginTop: 12 }}
                disabled={resLoading}
                onClick={() => void onFindResources()}
              >
                <Globe size={14} /> {resLoading ? "Finding…" : "Find videos & articles"}
              </button>
            </section>

            {/* Study materials */}
            <section className="trn-section">
              <h2 className="trn-section-head">
                <Spark size={17} /> Study materials
              </h2>
              <div className="trn-genrow">
                <button
                  type="button"
                  className="pill-btn sm primary"
                  onClick={() => openAdd("slides")}
                >
                  <Plus size={14} /> Add new content
                </button>
                {GENERATORS.map((k) => (
                  <button key={k} type="button" className="pill-btn sm" onClick={() => openAdd(k)}>
                    {KIND_META[k].icon} {KIND_META[k].label}
                  </button>
                ))}
              </div>

              {queue.length > 0 && (
                <div className="trn-queue">
                  <Spark size={14} />
                  <span>
                    Generating {queue.filter((j) => j.status === "running").length ? "now" : "soon"}
                    :
                  </span>
                  {queue.map((j) => (
                    <span key={j.id} className={`trn-qchip ${j.status}`}>
                      {KIND_META[j.kind].icon} {j.label}
                      {j.status === "running" ? " …" : ""}
                    </span>
                  ))}
                </div>
              )}

              {report.artifacts.length > 0 ? (
                <div className="trn-lib">
                  {report.artifacts.map((a) => (
                    <div key={a.id} className={`trn-lib-item ${completed.has(a.id) ? "done" : ""}`}>
                      <button
                        type="button"
                        className="trn-lib-open"
                        onClick={() => openArtifact(a)}
                      >
                        <span className="trn-lib-ic">{KIND_META[a.kind]?.icon}</span>
                        <span className="trn-lib-txt">
                          <span className="trn-lib-t">{a.title || KIND_META[a.kind]?.label}</span>
                          <span className="trn-lib-k">{KIND_META[a.kind]?.label}</span>
                        </span>
                      </button>
                      <DoneBtn id={a.id} />
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 12.5, color: "var(--text-3)", marginTop: 8 }}>
                  Turn your plan into a podcast, slides, a quiz, or an infographic — see and edit
                  the prompt, then it opens right here.
                </div>
              )}
            </section>

            {/* Community */}
            {community.length > 0 && (
              <section className="trn-section">
                <h2 className="trn-section-head">
                  <Users size={17} /> Popular with your peers
                </h2>
                <div className="trn-lib">
                  {community.map((c) => (
                    <div key={c.id} className={`trn-lib-item ${completed.has(c.id) ? "done" : ""}`}>
                      <button
                        type="button"
                        className="trn-lib-open"
                        onClick={() =>
                          openArtifact({ id: c.id, kind: c.kind, title: c.title, url: c.url })
                        }
                      >
                        <span className="trn-lib-ic">{KIND_META[c.kind]?.icon}</span>
                        <span className="trn-lib-txt">
                          <span className="trn-lib-t">{c.title || KIND_META[c.kind]?.label}</span>
                          <span className="trn-lib-k">{KIND_META[c.kind]?.label}</span>
                        </span>
                      </button>
                      <DoneBtn id={c.id} />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {report.skillGaps.length > 0 && (
              <section className="trn-section">
                <h2 className="trn-section-head">
                  <Trend size={17} /> Where to grow
                  <span className="trn-count">· {report.skillGaps.length}</span>
                </h2>
                <div className="trn-gaps">
                  {report.skillGaps.map((g) => (
                    <div
                      key={g.area}
                      className="trn-gap"
                      style={
                        {
                          "--sev-color": SEV_COLOR[g.severity] ?? "var(--text-4)",
                        } as React.CSSProperties
                      }
                    >
                      <div className="trn-gap-top">
                        <span className="trn-gap-title">{g.area}</span>
                        <span className="trn-sev">{SEV_LABEL[g.severity] ?? g.severity}</span>
                      </div>
                      <div className="trn-gap-why">{g.why}</div>
                      {g.evidence && <div className="trn-gap-ev">e.g. {g.evidence}</div>}
                      <div className="trn-item-actions">
                        <button
                          type="button"
                          className="ghost-btn xs"
                          onClick={() => openAdd("slides", g.area)}
                        >
                          Make a lesson
                        </button>
                        <button
                          type="button"
                          className="ghost-btn xs"
                          onClick={() => openAdd("quiz", g.area)}
                        >
                          Quiz me
                        </button>
                      </div>
                      <MadeChips topic={g.area} />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {report.productsToLearn.length > 0 && (
              <section className="trn-section">
                <h2 className="trn-section-head">
                  <Bolt size={17} /> Google products to learn
                </h2>
                <div className="trn-grid">
                  {report.productsToLearn.map((p) => (
                    <div key={p.product} className="trn-prod">
                      <div className="trn-prod-name">{p.product}</div>
                      <div className="trn-prod-why">{p.whyRelevant}</div>
                      {p.startHere && (
                        <div className="trn-prod-start">
                          <strong>Start here:</strong> {p.startHere}
                        </div>
                      )}
                      <div className="trn-item-actions">
                        <button
                          type="button"
                          className="ghost-btn xs"
                          onClick={() => openAdd("slides", p.product)}
                        >
                          Make a lesson
                        </button>
                        <button
                          type="button"
                          className="ghost-btn xs"
                          onClick={() => openAdd("audio", p.product)}
                        >
                          Podcast
                        </button>
                      </div>
                      <MadeChips topic={p.product} />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {report.recommendations.length > 0 && (
              <section className="trn-section">
                <h2 className="trn-section-head">
                  <Chart size={17} /> Recommended training
                </h2>
                <div className="trn-recs">
                  {report.recommendations.map((r) => (
                    <div key={r.title} className="trn-rec">
                      <span className="trn-rec-type">{r.type}</span>
                      <span>
                        <strong>{r.title}</strong> — {r.detail}
                        {safeHref(r.url) && (
                          <a href={safeHref(r.url)} target="_blank" rel="noopener noreferrer">
                            open ↗
                          </a>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {report.strengths.length > 0 && (
              <section className="trn-section">
                <h2 className="trn-section-head">
                  <Check size={17} /> You're doing well
                </h2>
                <div className="trn-strengths">
                  {report.strengths.map((s) => (
                    <div key={s.area} className="trn-strength">
                      <Check size={15} />
                      <span>
                        <strong>{s.area}:</strong> {s.note}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {report.quiz && report.quiz.length > 0 && (
              <section className="trn-section">
                <h2 className="trn-section-head">
                  <Question size={17} /> Quick practice
                </h2>
                <div className="trn-quiz">
                  {report.quiz.map((q, i) => (
                    <div key={q.q} className="trn-q">
                      <div className="trn-q-q">{q.q}</div>
                      {revealed.has(i) ? (
                        <div className="trn-q-a">{q.a}</div>
                      ) : (
                        <button
                          type="button"
                          className="ghost-btn xs"
                          style={{ marginTop: 6 }}
                          onClick={() => setRevealed((s) => new Set(s).add(i))}
                        >
                          Show answer
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            <div className="trn-more">
              <div className="trn-more-t">Want to keep learning?</div>
              <div className="trn-more-d">
                Get more topics and Google products worth exploring next.
              </div>
              <button
                type="button"
                className="pill-btn primary"
                disabled={moreLoading}
                onClick={() => void onMore()}
              >
                <Plus size={15} /> {moreLoading ? "Finding more…" : "Suggest more topics"}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Add new content modal */}
      {addOpen && (
        <Modal open onClose={() => setAddOpen(false)} title="Add new content" width={600}>
          <div className="trn-add">
            <div className="trn-add-label">Type</div>
            <div className="trn-add-types">
              {GENERATORS.map((k) => (
                <button
                  key={k}
                  type="button"
                  className={`pill-btn sm ${addKind === k ? "primary" : ""}`}
                  onClick={() => {
                    setAddKind(k);
                    setAddPrompt(
                      addTopic ? `Focus on: ${addTopic}. ${DEFAULT_PROMPT[k]}` : DEFAULT_PROMPT[k],
                    );
                  }}
                >
                  {KIND_META[k].icon} {KIND_META[k].label}
                </button>
              ))}
            </div>

            {addKind === "audio" && (
              <>
                <div className="trn-add-label" style={{ marginTop: 14 }}>
                  Length
                </div>
                <div className="trn-add-types">
                  {AUDIO_LENGTHS.map((l) => (
                    <button
                      key={l.value}
                      type="button"
                      className={`pill-btn sm ${addLength === l.value ? "primary" : ""}`}
                      onClick={() => setAddLength(l.value)}
                    >
                      {l.label} <span style={{ opacity: 0.7 }}>· {l.hint}</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            <div className="trn-add-label" style={{ marginTop: 14 }}>
              Language
            </div>
            <select
              className="setup-input"
              value={contentLang}
              onChange={(e) => changeContentLang(e.target.value as "auto" | LangCode)}
            >
              <option value="auto">Auto (my default)</option>
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label} · {l.labelEn}
                </option>
              ))}
            </select>
            <div style={{ fontSize: 12, color: "var(--text-4)", marginTop: 4 }}>
              Applies to every material you generate — your choice is remembered.
            </div>

            <div className="trn-add-label" style={{ marginTop: 14 }}>
              Prompt (edit to control what's generated)
            </div>
            <textarea
              className="setup-input"
              rows={4}
              value={addPrompt}
              onChange={(e) => setAddPrompt(e.target.value)}
            />

            <div className="trn-add-related">
              <div className="trn-add-label" style={{ marginBottom: 8 }}>
                Or use something that already exists
              </div>
              {related.length === 0 ? (
                <button
                  type="button"
                  className="ghost-btn xs"
                  disabled={relatedLoading}
                  onClick={() => void loadRelated()}
                >
                  <Globe size={13} />{" "}
                  {relatedLoading ? "Searching…" : "Find related videos & articles"}
                </button>
              ) : (
                <div className="trn-add-reslist">
                  {related.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      className="trn-add-res"
                      onClick={() => {
                        setAddOpen(false);
                        openResource(r);
                      }}
                    >
                      {r.thumbnail ? (
                        <img src={r.thumbnail} alt="" loading="lazy" />
                      ) : (
                        <span className="trn-add-res-ic">{resIcon(r.type)}</span>
                      )}
                      <span className="trn-add-res-t">{r.title}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {addErr && (
              <div className="trn-error" style={{ marginTop: 14 }}>
                {addErr}
              </div>
            )}
            <div style={{ fontSize: 12, color: "var(--text-4)", marginTop: 12 }}>
              It's added to your generation queue and appears in Study materials when ready — you
              can close this and keep working.
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              <button type="button" className="ghost-btn" onClick={() => setAddOpen(false)}>
                Cancel
              </button>
              <button type="button" className="pill-btn primary" onClick={runGenerate}>
                Generate{" "}
                {addKind === "audio" && addLength !== "standard"
                  ? (AUDIO_LENGTHS.find((l) => l.value === addLength)?.label ?? "Podcast")
                  : KIND_META[addKind].label}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* In-page viewer */}
      {viewer && (
        <Modal
          open
          onClose={closeViewer}
          title={viewer.item.title || "Material"}
          width={viewer.item.mode === "artifact" && viewer.item.kind === "audio" ? 460 : 900}
        >
          <div className="trn-viewer">
            {viewer.item.mode === "resource" ? (
              viewer.item.rtype === "video" && viewer.item.videoId ? (
                <iframe
                  title={viewer.item.title || "Video"}
                  src={`https://www.youtube.com/embed/${viewer.item.videoId}?autoplay=1&rel=0`}
                  className="trn-viewer-frame"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                />
              ) : (
                <>
                  <iframe
                    title={viewer.item.title || "Article"}
                    src={viewer.item.url}
                    className="trn-viewer-frame"
                    sandbox="allow-scripts allow-same-origin allow-popups"
                  />
                  <div className="trn-viewer-note">
                    Some sites can't be shown in-app.{" "}
                    {safeHref(viewer.item.url) && (
                      <a href={safeHref(viewer.item.url)} target="_blank" rel="noopener noreferrer">
                        Open in a new tab ↗
                      </a>
                    )}
                  </div>
                </>
              )
            ) : viewer.loading ? (
              <div className="trn-empty">Loading…</div>
            ) : viewer.error ? (
              <div className="trn-error">{viewer.error}</div>
            ) : viewer.item.kind === "audio" ? (
              // biome-ignore lint/a11y/useMediaCaption: generated narration has no caption track
              <audio controls autoPlay src={viewer.blobUrl} style={{ width: "100%" }} />
            ) : viewer.item.kind === "image" ? (
              <img
                src={viewer.blobUrl}
                alt={viewer.item.title || "Infographic"}
                style={{ width: "100%", borderRadius: 8 }}
              />
            ) : (
              <iframe
                title={viewer.item.title || "Material"}
                src={viewer.blobUrl}
                sandbox=""
                className="trn-viewer-frame"
              />
            )}
            {viewer.item.mode === "artifact" && viewer.blobUrl && (
              <div
                style={{
                  marginTop: 10,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <DoneBtn id={viewer.item.id} />
                <a
                  className="ghost-btn xs"
                  href={viewer.blobUrl}
                  download={viewer.item.title || viewer.item.kind}
                >
                  Download
                </a>
              </div>
            )}
            {viewer.item.mode === "resource" && <DoneBtn id={viewer.item.id} />}
          </div>
        </Modal>
      )}

      {/* Floating "content studio" progress widget */}
      {(queue.length > 0 || recentDone.length > 0) && (
        <div className="trn-widget">
          <div className="trn-widget-head">
            <span className="trn-widget-title">
              <Spark size={14} /> Content studio
              {queue.length > 0 && <span className="trn-widget-count">{queue.length}</span>}
            </span>
            <div style={{ display: "flex", gap: 4 }}>
              <button
                type="button"
                className="icon-btn icon-btn-sm"
                title={widgetMin ? "Expand" : "Collapse"}
                onClick={() => setWidgetMin((v) => !v)}
              >
                {widgetMin ? "▸" : "▾"}
              </button>
              {queue.length === 0 && (
                <button
                  type="button"
                  className="icon-btn icon-btn-sm"
                  title="Dismiss"
                  onClick={() => setRecentDone([])}
                >
                  ×
                </button>
              )}
            </div>
          </div>
          {!widgetMin && (
            <div className="trn-widget-body">
              {queue.map((j) => (
                <div key={j.id} className="trn-widget-row">
                  {j.status === "running" ? (
                    <Spinner size={14} />
                  ) : (
                    <span className="trn-widget-dot" />
                  )}
                  <span className="trn-widget-lbl">
                    {KIND_META[j.kind].label}
                    {j.topic ? ` · ${j.topic}` : ""}
                  </span>
                  <span className="trn-widget-state">
                    {j.status === "running" ? "generating…" : "queued"}
                  </span>
                </div>
              ))}
              {recentDone.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className="trn-widget-done"
                  onClick={() => openArtifact(a)}
                >
                  <Check size={13} />
                  <span className="trn-widget-lbl">
                    {a.title || KIND_META[a.kind]?.label}
                  </span>
                  <span className="trn-widget-open">open</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
